import { Link } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import { useCompanyListScreen } from './hooks/useCompanyListScreen'
import MasterApprovalStatusBadge from '../../approval/components/MasterApprovalStatusBadge'

const STATUS_TABS = [
  { id: 'all', label: 'All Entities' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending Review' },
  { id: 'archive', label: 'Archive' },
]

function badgeStyle(status) {
  if (status === 'Active') return 'bg-emerald-100 text-emerald-800'
  if (status === 'Pending Review') return 'bg-amber-100 text-amber-700'
  if (status === 'Archive / Deactive') return 'bg-slate-200 text-slate-600'
  return 'bg-surface-container-high text-on-surface-variant'
}

function badgeDotStyle(status) {
  if (status === 'Active') return 'bg-emerald-600'
  if (status === 'Pending Review') return 'bg-amber-600'
  if (status === 'Archive / Deactive') return 'bg-slate-500'
  return 'bg-on-surface-variant'
}

function getInitials(name) {
  const clean = String(name || '').trim()
  if (!clean) return 'NA'
  const first = clean[0]
  const last = clean[clean.length - 1]
  return `${first}${last}`.toUpperCase()
}

function getAvatarClass(name) {
  const palette = [
    'bg-blue-100 text-blue-800',
    'bg-slate-200 text-slate-700',
    'bg-cyan-100 text-cyan-800',
    'bg-rose-100 text-rose-700',
    'bg-indigo-100 text-indigo-800',
    'bg-emerald-100 text-emerald-700',
  ]

  const value = String(name || '')
  const total = value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return palette[total % palette.length]
}



function PreviewModal({ state, actions }) {
  if (!state.isPreviewOpen) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#191c1e]/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-outline-variant/40 bg-surface-container-lowest shadow-ambient">
        <div className="flex items-center justify-between border-b border-outline-variant/30 px-6 py-4">
          <h3 className="text-xl font-bold text-on-surface">Company Preview</h3>
          <button
            type="button"
            onClick={actions.closePreview}
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-low"
            aria-label="Close preview"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        <div className="space-y-4 px-6 py-5 text-sm">
          {state.isPreviewLoading ? (
            <p className="py-8 text-center text-on-surface-variant">Loading company details...</p>
          ) : state.previewError ? (
            <p className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-error">{state.previewError}</p>
          ) : (
            <>
              <div className="flex items-center gap-4">
                {state.previewData?.logoUrl ? (
                  <img
                    src={state.previewData.logoUrl}
                    alt={`${state.previewData?.name || 'Company'} logo`}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold ${getAvatarClass(
                      state.previewData?.name,
                    )}`}
                  >
                    {getInitials(state.previewData?.name)}
                  </div>
                )}

                <div>
                  <p className="text-lg font-bold text-on-surface">{state.previewData?.name || '-'}</p>
                  <p className="text-xs uppercase tracking-[0.14em] text-on-surface-variant">
                    {state.previewData?.company_code || '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-surface-container-low px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.12em] text-on-surface-variant">GST Number</p>
                  <p className="mt-1 font-semibold text-on-surface">{state.previewData?.gst_tax_number || '-'}</p>
                </div>
                <div className="rounded-xl bg-surface-container-low px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.12em] text-on-surface-variant">Corporate Email</p>
                  <p className="mt-1 font-semibold text-on-surface">{state.previewData?.corporate_email || '-'}</p>
                </div>
                <div className="rounded-xl bg-surface-container-low px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.12em] text-on-surface-variant">Support Phone</p>
                  <p className="mt-1 font-semibold text-on-surface">{state.previewData?.support_phone || '-'}</p>
                </div>
                <div className="rounded-xl bg-surface-container-low px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.12em] text-on-surface-variant">Website</p>
                  <p className="mt-1 font-semibold text-on-surface">{state.previewData?.company_website || '-'}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function CompanyMasterListScreen() {
  const { state, actions } = useCompanyListScreen()

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
        <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-4xl">
            <h1 className="text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">Company Master</h1>
            <p className="mt-2 text-base text-on-surface-variant">
              Centralized administration for all enterprise entities. Manage corporate records, compliance identities,
              and operational status.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="inline-flex items-center rounded-[1.75rem] bg-surface-container-low p-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => actions.setStatusFilter(tab.id)}
                  className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition-all ${
                    state.statusFilter === tab.id
                      ? 'bg-surface-container-lowest text-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-outline-variant/35 bg-surface-container-low p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Company Name</span>
              <div className="relative">
                <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                  search
                </span>
                <input
                  type="text"
                  placeholder="Search company..."
                  value={state.searchText}
                  onChange={(event) => actions.setSearchText(event.target.value)}
                  className="w-full rounded-xl border-0 bg-surface-container-lowest py-3 pl-11 pr-4 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
                />
              </div>
            </label>

            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">GST Number</span>
              <input
                type="text"
                placeholder="Enter GSTIN..."
                value={state.gstText}
                onChange={(event) => actions.setGstText(event.target.value)}
                className="w-full rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Date Range</span>
              <div className="grid grid-cols-2 gap-2">
                <AppDateInput
                  value={state.fromDate}
                  onChange={actions.setFromDate}
                  inputClassName="w-full rounded-xl border-0 bg-surface-container-lowest px-3 py-3 text-sm text-on-surface shadow-none"
                />
                <AppDateInput
                  value={state.toDate}
                  onChange={actions.setToDate}
                  inputClassName="w-full rounded-xl border-0 bg-surface-container-lowest px-3 py-3 text-sm text-on-surface shadow-none"
                />
              </div>
            </label>
          </div>
        </section>

        {state.error ? (
          <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
        ) : null}

        <section className="overflow-hidden rounded-[2rem] border border-outline-variant/35 bg-surface-container-lowest">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left">
              <thead>
                <tr className="bg-surface-container-low/60 text-[11px] uppercase tracking-[0.14em] text-on-surface-variant">
                  <th className="px-7 py-5 font-black">Company Name</th>
                  <th className="px-6 py-5 font-black">Company Code</th>
                  <th className="px-6 py-5 font-black">GST Number</th>
                  <th className="px-6 py-5 font-black">Status</th>
                  <th className="px-6 py-5 font-black">Created By</th>
                  <th className="px-6 py-5 font-black">Created Date</th>
                  <th className="px-6 py-5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-surface-variant/35">
                {state.isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                      Loading companies...
                    </td>
                  </tr>
                ) : state.rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                      No companies found for selected filters.
                    </td>
                  </tr>
                ) : (
                  state.rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-surface-container-low/30">
                      <td className="px-7 py-5">
                        <div className="flex items-center gap-4">
                          {row.logoUrl ? (
                            <img
                              src={row.logoUrl}
                              alt={`${row.name} logo`}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${getAvatarClass(
                                row.name,
                              )}`}
                            >
                              {getInitials(row.name)}
                            </div>
                          )}
                          <span className="text-lg font-bold leading-tight text-on-surface">{row.name}</span>
                        </div>
                      </td>

                      <td className="px-6 py-5 text-sm font-semibold font-mono text-primary">{row.companyCode}</td>

                      <td className="px-6 py-5 text-sm text-on-surface-variant">{row.gstTaxNumber}</td>

                      <td className="px-6 py-5">
                        <MasterApprovalStatusBadge
                          status={row.status === 'Active' ? 'APPROVED' : (row.status === 'Pending Review' ? 'PENDING' : 'ARCHIVED')}
                        />
                      </td>

                      <td className="px-6 py-5 text-sm text-on-surface">{row.createdBy}</td>
                      <td className="px-6 py-5 text-sm text-on-surface">{row.createdAt}</td>

                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => actions.openPreview(row.id)}
                            className="rounded-full p-2 text-primary transition-colors hover:bg-surface-container-high"
                            title="Preview company"
                          >
                            <span className="material-symbols-outlined">visibility</span>
                          </button>

                          <Link
                            to={`/masters/company/entry?id=${row.id}`}
                            className="rounded-full p-2 text-primary transition-colors hover:bg-surface-container-high"
                            title="Edit company"
                          >
                            <span className="material-symbols-outlined">edit</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-7 py-5 lg:flex-row lg:items-center lg:justify-between">
            <span className="text-sm text-on-surface-variant">
              Showing {state.rangeStart} to {state.rangeEnd} of {state.totalCount} companies
            </span>

            <AppPagination
              page={state.page}
              totalPages={state.totalPages}
              onPageChange={actions.goToPage}
              onPrevious={actions.goToPreviousPage}
              onNext={actions.goToNextPage}
              canPrevious={state.page > 1}
              canNext={Boolean(state.hasNext) || state.page < state.totalPages}
            />
          </div>
        </section>
      </div>

      <PreviewModal state={state} actions={actions} />
    </>
  )
}



